/**
 * Communications Ledger Admin Panel
 * 
 * Admin diagnostic tool for viewing all outbound communications.
 * Features:
 * - Filterable table with server-side pagination
 * - Detail drawer showing all fields
 * - Color-coded badges for channel and status
 * 
 * This is a read-only diagnostic view - no sending or modification.
 */

import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../../components/ui/sheet";
import { toast } from "sonner";
import {
  Envelope,
  ChatCircleText,
  DeviceMobile,
  Bell,
  Megaphone,
  MagnifyingGlass,
  ArrowClockwise,
  CaretLeft,
  CaretRight,
  X,
  CheckCircle,
  Warning,
  Clock,
  XCircle,
  Prohibit,
  UserMinus,
} from "phosphor-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Channel icons and colors
const CHANNEL_CONFIG = {
  email: { icon: Envelope, color: "bg-blue-500/20 text-blue-400 border-blue-500/30", label: "Email" },
  sms: { icon: ChatCircleText, color: "bg-green-500/20 text-green-400 border-green-500/30", label: "SMS" },
  in_app: { icon: Bell, color: "bg-purple-500/20 text-purple-400 border-purple-500/30", label: "In-App" },
  system: { icon: DeviceMobile, color: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30", label: "System" },
  admin_broadcast: { icon: Megaphone, color: "bg-orange-500/20 text-orange-400 border-orange-500/30", label: "Broadcast" },
};

// Status icons and colors
const STATUS_CONFIG = {
  pending: { icon: Clock, color: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30", label: "Pending" },
  sent: { icon: CheckCircle, color: "bg-blue-500/20 text-blue-400 border-blue-500/30", label: "Sent" },
  delivered: { icon: CheckCircle, color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30", label: "Delivered" },
  failed: { icon: XCircle, color: "bg-red-500/20 text-red-400 border-red-500/30", label: "Failed" },
  blocked: { icon: Prohibit, color: "bg-orange-500/20 text-orange-400 border-orange-500/30", label: "Blocked" },
  suppressed: { icon: UserMinus, color: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30", label: "Suppressed" },
};

// Pretty JSON component
function PrettyJson({ data, maxHeight = "300px" }) {
  if (data === null || data === undefined) {
    return <span className="text-zinc-500 italic">null</span>;
  }
  
  let formatted;
  if (typeof data === "string") {
    try {
      formatted = JSON.stringify(JSON.parse(data), null, 2);
    } catch {
      formatted = data;
    }
  } else {
    formatted = JSON.stringify(data, null, 2);
  }
  
  return (
    <pre 
      className="bg-zinc-900 p-3 rounded text-xs text-zinc-300 overflow-auto font-mono"
      style={{ maxHeight }}
    >
      {formatted}
    </pre>
  );
}

export function CommsLedgerPanel() {
  // Filter state
  const [filters, setFilters] = useState({
    tenant_id: "",
    recipient: "",
    channel: "",
    normalized_status: "",
    comm_type: "",
    start_date: "",
    end_date: "",
  });
  
  // Table state
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pages: 1,
    total: 0,
    limit: 50,
    has_more: false,
  });
  
  // Detail drawer state
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  
  // Fetch ledger entries
  const fetchEntries = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", page.toString());
      params.set("limit", "50");
      
      if (filters.tenant_id) params.set("tenant_id", filters.tenant_id);
      if (filters.recipient) params.set("recipient", filters.recipient);
      if (filters.channel) params.set("channel", filters.channel);
      if (filters.normalized_status) params.set("normalized_status", filters.normalized_status);
      if (filters.comm_type) params.set("comm_type", filters.comm_type);
      if (filters.start_date) params.set("start_date", filters.start_date);
      if (filters.end_date) params.set("end_date", filters.end_date);
      
      const res = await fetch(`${API}/admin/system/comms-ledger?${params}`, {
        credentials: "include",
      });
      
      if (!res.ok) {
        throw new Error("Failed to fetch ledger entries");
      }
      
      const data = await res.json();
      setItems(data.items || []);
      setPagination({
        page: data.page || 1,
        pages: data.pages || 1,
        total: data.total || 0,
        limit: data.limit || 50,
        has_more: data.has_more || false,
      });
    } catch (err) {
      toast.error("Failed to load communications ledger");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filters]);
  
  // Fetch single entry detail
  const fetchEntryDetail = async (entryId) => {
    setLoadingDetail(true);
    try {
      const res = await fetch(`${API}/admin/system/comms-ledger/${entryId}`, {
        credentials: "include",
      });
      
      if (!res.ok) {
        throw new Error("Failed to fetch entry details");
      }
      
      const data = await res.json();
      setSelectedEntry(data);
      setDrawerOpen(true);
    } catch (err) {
      toast.error("Failed to load entry details");
      console.error(err);
    } finally {
      setLoadingDetail(false);
    }
  };
  
  // Initial load
  useEffect(() => {
    fetchEntries(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };
  
  // Apply filters
  const handleApplyFilters = () => {
    fetchEntries(1);
  };
  
  // Clear filters
  const handleClearFilters = () => {
    setFilters({
      tenant_id: "",
      recipient: "",
      channel: "",
      normalized_status: "",
      comm_type: "",
      start_date: "",
      end_date: "",
    });
    // Re-fetch with cleared filters
    setTimeout(() => fetchEntries(1), 0);
  };
  
  // Pagination
  const handlePrevPage = () => {
    if (pagination.page > 1) {
      fetchEntries(pagination.page - 1);
    }
  };
  
  const handleNextPage = () => {
    if (pagination.has_more) {
      fetchEntries(pagination.page + 1);
    }
  };
  
  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const date = new Date(dateStr);
      return date.toLocaleString();
    } catch {
      return dateStr;
    }
  };
  
  // Truncate text
  const truncate = (str, len = 30) => {
    if (!str) return "-";
    return str.length > len ? str.slice(0, len) + "..." : str;
  };
  
  return (
    <div className="space-y-6">
      {/* Filter Card */}
      <Card className="bg-zinc-800 border-zinc-700">
        <CardHeader className="pb-4">
          <CardTitle className="text-white flex items-center gap-2">
            <MagnifyingGlass size={20} />
            Communications Ledger
          </CardTitle>
          <CardDescription className="text-zinc-400">
            Diagnostic view of all outbound communications (default: last 7 days)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Filter Row 1 */}
          <div className="grid grid-cols-4 gap-4 mb-4">
            <div>
              <Label className="text-zinc-400 text-xs">Tenant ID</Label>
              <Input
                placeholder="Exact match"
                value={filters.tenant_id}
                onChange={(e) => handleFilterChange("tenant_id", e.target.value)}
                className="bg-zinc-900 border-zinc-600 text-white mt-1"
                data-testid="comms-filter-tenant"
              />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">Recipient</Label>
              <Input
                placeholder="Email or phone (substring)"
                value={filters.recipient}
                onChange={(e) => handleFilterChange("recipient", e.target.value)}
                className="bg-zinc-900 border-zinc-600 text-white mt-1"
                data-testid="comms-filter-recipient"
              />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">Channel</Label>
              <Select
                value={filters.channel || "__all__"}
                onValueChange={(v) => handleFilterChange("channel", v === "__all__" ? "" : v)}
              >
                <SelectTrigger className="bg-zinc-900 border-zinc-600 text-white mt-1" data-testid="comms-filter-channel">
                  <SelectValue placeholder="All channels" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="__all__">All</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="sms">SMS</SelectItem>
                  <SelectItem value="in_app">In-App</SelectItem>
                  <SelectItem value="system">System</SelectItem>
                  <SelectItem value="admin_broadcast">Broadcast</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">Status</Label>
              <Select
                value={filters.normalized_status || "__all__"}
                onValueChange={(v) => handleFilterChange("normalized_status", v === "__all__" ? "" : v)}
              >
                <SelectTrigger className="bg-zinc-900 border-zinc-600 text-white mt-1" data-testid="comms-filter-status">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="__all__">All</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="delivered">Delivered</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="blocked">Blocked</SelectItem>
                  <SelectItem value="suppressed">Suppressed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Filter Row 2 */}
          <div className="grid grid-cols-4 gap-4 mb-4">
            <div>
              <Label className="text-zinc-400 text-xs">Type</Label>
              <Input
                placeholder="e.g. invoice.sent"
                value={filters.comm_type}
                onChange={(e) => handleFilterChange("comm_type", e.target.value)}
                className="bg-zinc-900 border-zinc-600 text-white mt-1"
                data-testid="comms-filter-type"
              />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">Start Date</Label>
              <Input
                type="date"
                value={filters.start_date}
                onChange={(e) => handleFilterChange("start_date", e.target.value)}
                className="bg-zinc-900 border-zinc-600 text-white mt-1"
                data-testid="comms-filter-start"
              />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">End Date</Label>
              <Input
                type="date"
                value={filters.end_date}
                onChange={(e) => handleFilterChange("end_date", e.target.value)}
                className="bg-zinc-900 border-zinc-600 text-white mt-1"
                data-testid="comms-filter-end"
              />
            </div>
            <div className="flex items-end gap-2">
              <Button
                onClick={handleApplyFilters}
                disabled={loading}
                className="bg-emerald-600 hover:bg-emerald-700 text-white flex-1"
                data-testid="comms-filter-apply"
              >
                {loading ? <ArrowClockwise size={16} className="animate-spin" /> : <MagnifyingGlass size={16} />}
                <span className="ml-2">Apply</span>
              </Button>
              <Button
                onClick={handleClearFilters}
                variant="outline"
                className="border-zinc-600 text-zinc-300 hover:bg-zinc-700"
                data-testid="comms-filter-clear"
              >
                <X size={16} />
              </Button>
            </div>
          </div>
          
          {/* Results Info */}
          <div className="flex items-center justify-between text-sm text-zinc-400 border-t border-zinc-700 pt-4">
            <span>
              Showing {items.length} of {pagination.total} entries
              {!filters.start_date && !filters.end_date && " (last 7 days)"}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handlePrevPage}
                disabled={pagination.page <= 1 || loading}
                className="text-zinc-400 hover:text-white"
              >
                <CaretLeft size={16} />
              </Button>
              <span>Page {pagination.page} of {pagination.pages}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleNextPage}
                disabled={!pagination.has_more || loading}
                className="text-zinc-400 hover:text-white"
              >
                <CaretRight size={16} />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Results Table */}
      <Card className="bg-zinc-800 border-zinc-700">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-700 bg-zinc-900">
                  <th className="text-left p-3 text-zinc-400 font-medium">Created</th>
                  <th className="text-left p-3 text-zinc-400 font-medium">Tenant</th>
                  <th className="text-left p-3 text-zinc-400 font-medium">Channel</th>
                  <th className="text-left p-3 text-zinc-400 font-medium">Status</th>
                  <th className="text-left p-3 text-zinc-400 font-medium">Recipient</th>
                  <th className="text-left p-3 text-zinc-400 font-medium">Subject</th>
                  <th className="text-left p-3 text-zinc-400 font-medium">Type</th>
                  <th className="text-left p-3 text-zinc-400 font-medium">Provider</th>
                  <th className="text-left p-3 text-zinc-400 font-medium">Campaign</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={9} className="p-8 text-center text-zinc-500">
                      <ArrowClockwise size={24} className="animate-spin mx-auto mb-2" />
                      Loading...
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="p-8 text-center text-zinc-500">
                      No entries found matching filters
                    </td>
                  </tr>
                ) : (
                  items.map((item) => {
                    const channelCfg = CHANNEL_CONFIG[item.channel] || CHANNEL_CONFIG.system;
                    const statusCfg = STATUS_CONFIG[item.normalized_status] || STATUS_CONFIG.pending;
                    const ChannelIcon = channelCfg.icon;
                    const StatusIcon = statusCfg.icon;
                    
                    return (
                      <tr
                        key={item._id}
                        onClick={() => fetchEntryDetail(item._id)}
                        className="border-b border-zinc-700/50 hover:bg-zinc-700/30 cursor-pointer transition-colors"
                        data-testid={`comms-row-${item._id}`}
                      >
                        <td className="p-3 text-zinc-300 text-xs font-mono">
                          {formatDate(item.created_at)}
                        </td>
                        <td className="p-3 text-zinc-400 text-xs font-mono">
                          {truncate(item.tenant_id, 12)}
                        </td>
                        <td className="p-3">
                          <Badge className={`${channelCfg.color} text-xs`}>
                            <ChannelIcon size={12} className="mr-1" />
                            {channelCfg.label}
                          </Badge>
                        </td>
                        <td className="p-3">
                          <Badge className={`${statusCfg.color} text-xs`}>
                            <StatusIcon size={12} className="mr-1" />
                            {statusCfg.label}
                          </Badge>
                        </td>
                        <td className="p-3 text-zinc-300 text-xs font-mono">
                          {truncate(item.recipient, 25)}
                        </td>
                        <td className="p-3 text-zinc-400 text-xs">
                          {truncate(item.subject, 30)}
                        </td>
                        <td className="p-3 text-zinc-400 text-xs">
                          {truncate(item.type, 20)}
                        </td>
                        <td className="p-3 text-zinc-500 text-xs">
                          {item.provider || "-"}
                        </td>
                        <td className="p-3 text-zinc-500 text-xs font-mono">
                          {truncate(item.campaign_id, 10)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      
      {/* Detail Drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent className="bg-zinc-900 border-zinc-700 w-[500px] sm:w-[600px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-white">Communication Details</SheetTitle>
            <SheetDescription className="text-zinc-400">
              Full details for ledger entry
            </SheetDescription>
          </SheetHeader>
          
          {loadingDetail ? (
            <div className="flex items-center justify-center py-12">
              <ArrowClockwise size={32} className="animate-spin text-zinc-500" />
            </div>
          ) : selectedEntry ? (
            <div className="mt-6 space-y-4">
              {/* Status badges */}
              <div className="flex gap-2 mb-4">
                {(() => {
                  const channelCfg = CHANNEL_CONFIG[selectedEntry.channel] || CHANNEL_CONFIG.system;
                  const statusCfg = STATUS_CONFIG[selectedEntry.normalized_status] || STATUS_CONFIG.pending;
                  const ChannelIcon = channelCfg.icon;
                  const StatusIcon = statusCfg.icon;
                  return (
                    <>
                      <Badge className={channelCfg.color}>
                        <ChannelIcon size={14} className="mr-1" />
                        {channelCfg.label}
                      </Badge>
                      <Badge className={statusCfg.color}>
                        <StatusIcon size={14} className="mr-1" />
                        {statusCfg.label}
                      </Badge>
                    </>
                  );
                })()}
              </div>
              
              {/* Fields */}
              <div className="space-y-3">
                <DetailField label="Entry ID" value={selectedEntry._id} mono />
                <DetailField label="Tenant ID" value={selectedEntry.tenant_id} mono />
                <DetailField label="Channel" value={selectedEntry.channel} />
                <DetailField label="Type" value={selectedEntry.type} />
                <DetailField label="Recipient" value={selectedEntry.recipient} mono />
                <DetailField label="Subject" value={selectedEntry.subject} />
                <DetailField label="Template ID" value={selectedEntry.template_id} mono />
                <DetailField label="Campaign ID" value={selectedEntry.campaign_id} mono />
                <DetailField label="Provider" value={selectedEntry.provider} />
                <DetailField label="Provider Message ID" value={selectedEntry.provider_message_id} mono />
                <DetailField label="Status" value={selectedEntry.normalized_status} />
                <DetailField label="Raw Provider Status" value={selectedEntry.raw_provider_status} />
                <DetailField label="Error Message" value={selectedEntry.error_message} error />
                <DetailField label="Created At" value={formatDate(selectedEntry.created_at)} />
                <DetailField label="Updated At" value={formatDate(selectedEntry.updated_at)} />
              </div>
              
              {/* Metadata JSON */}
              <div className="pt-4 border-t border-zinc-700">
                <Label className="text-zinc-400 text-xs mb-2 block">Metadata</Label>
                <PrettyJson data={selectedEntry.metadata} />
              </div>
              
              {/* Raw Provider Response JSON */}
              <div className="pt-4 border-t border-zinc-700">
                <Label className="text-zinc-400 text-xs mb-2 block">Raw Provider Response</Label>
                <PrettyJson data={selectedEntry.raw_provider_response} />
              </div>
            </div>
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}

// Helper component for detail fields
function DetailField({ label, value, mono = false, error = false }) {
  const displayValue = value ?? "-";
  return (
    <div className="flex flex-col">
      <span className="text-zinc-500 text-xs">{label}</span>
      <span 
        className={`text-sm ${error && value ? "text-red-400" : "text-zinc-300"} ${mono ? "font-mono" : ""}`}
      >
        {displayValue}
      </span>
    </div>
  );
}

export default CommsLedgerPanel;
